import React from 'react';
import { motion } from 'framer-motion';
import { Check } from '../../lib/lucide-proxy';
import Button from '../ui/Button';
import { PLAN_FEATURES } from '../../config/stripe';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const Pricing = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const handleSubscribe = (planType) => {
    if (!user) {
      navigate('/register');
      return;
    }

    // User is logged in
    if (profile?.role === 'brewery_owner') {
      // Redirect to billing to manage subscription
      navigate('/admin/billing');
    } else if (profile?.role === 'admin') {
      navigate('/admin/billing');
    } else {
      // Customer trying to subscribe as brewery
      toast.error('Sua conta é de Cliente. Crie uma conta de Cervejaria para assinar planos.', {
        duration: 5000,
        icon: '🍺'
      });
      // Optional: redirect to profile or logout
    }
  };

  return (
    <section id="precos" className="py-16 bg-cream-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-dark-900 mb-4">
              Planos para Cervejarias
            </h2>
            <p className="text-lg text-dark-600 max-w-2xl mx-auto">
              Escolha o plano ideal para o seu negócio. Comece grátis e cresça conosco.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* FREE PLAN */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-soft border-2 border-transparent hover:border-gray-200 transition-colors flex flex-col"
          >
            <h3 className="text-2xl font-display font-bold text-dark-900 mb-2">
              {PLAN_FEATURES.FREE.name}
            </h3>
            <div className="flex items-baseline mb-6">
              <span className="text-4xl font-bold text-dark-900">{PLAN_FEATURES.FREE.price}</span>
              <span className="text-dark-500 ml-2">/mês</span>
            </div>
            <p className="text-dark-600 mb-8">
              Ideal para quem está começando e quer experimentar a plataforma.
            </p>
            <ul className="space-y-4 mb-8 flex-1">
              {PLAN_FEATURES.FREE.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-dark-700">{feature}</span>
                </li>
              ))}
              {PLAN_FEATURES.FREE.limitations.map((limitation, index) => (
                <li key={index} className="flex items-start opacity-50">
                  <span className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span className="text-dark-700">{limitation}</span>
                </li>
              ))}
            </ul>
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => handleSubscribe('free')}
            >
              {user && profile?.role === 'brewery_owner' ? 'Ver Meu Plano' : 'Começar Grátis'}
            </Button>
          </motion.div>

          {/* PRO PLAN */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-dark-900 rounded-2xl p-8 shadow-large border-2 border-primary-500 relative overflow-hidden flex flex-col"
          >
            <div className="absolute top-0 right-0 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
              Recomendado
            </div>
            <h3 className="text-2xl font-display font-bold text-white mb-2">
              {PLAN_FEATURES.PRO.name}
            </h3>
            <div className="flex items-baseline mb-6">
              <span className="text-4xl font-bold text-white">{PLAN_FEATURES.PRO.price}</span>
              <span className="text-gray-400 ml-2">/mês</span>
            </div>
            <p className="text-gray-300 mb-8">
              Para cervejarias que querem escalar suas vendas e fidelizar clientes.
            </p>
            <ul className="space-y-4 mb-8 flex-1">
              {PLAN_FEATURES.PRO.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-200">{feature}</span>
                </li>
              ))}
            </ul>
            <Button 
              variant="primary" 
              className="w-full" 
              onClick={() => handleSubscribe('pro')}
            >
              {user && profile?.role === 'brewery_owner' ? 'Gerenciar Assinatura' : 'Assinar Agora'}
            </Button>
            <p className="text-center text-gray-400 text-sm mt-4">
              14 dias de teste grátis
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;